import { useMemo, useState } from 'react';
import { Repeat } from 'lucide-react';
import { nomCourt, questionsRepassees } from './margeDuHasard';
import { Carte } from './ElementsProgression';

/**
 * ÉTAPE 2, À L'ESSAI (11/10/2026) : la classe sur tous ses QCM de suivi, dans Pilotage, Résultats nationaux, à
 * côté de la vue de septembre qui reste celle par défaut (EvalNational).
 *
 * Épreuve de Musk du 12/10/2026 : seulement ce qui sert à refaire les groupes. Chaque item de septembre est un
 * savoir ; pour chaque question repassée depuis, les élèves qui l'ont acquise, ceux à confirmer, les fragiles et
 * ceux à revoir. Le tableau des QCM comparés à septembre et la marge du hasard ne s'affichent plus.
 */
const NB = ' ';
// Les noms des élèves à revoir et des fragiles sont affichés d'office ; ceux des autres statuts, d'un clic
const STATUTS_AFFICHES = [
  { cle: 'a_revoir', libelle: 'À revoir', liste: 'À revoir', style: 'bg-red-100 text-red-700' },
  { cle: 'fragile', libelle: 'Fragile', liste: 'Fragiles', style: 'bg-amber-100 text-amber-800' },
  { cle: 'a_confirmer', libelle: 'À confirmer', liste: 'À confirmer', style: 'bg-sky-100 text-sky-700' },
  { cle: 'acquis', libelle: 'Acquis', liste: 'Acquis', style: 'bg-emerald-100 text-emerald-700' },
];
const OUVERTS_D_OFFICE = ['a_revoir', 'fragile'];
const PREMIERES_QUESTIONS = 8;

const nomCourtDeLItem = (item) => nomCourt(item.domaine, item.texte, item.num);

const LigneQuestion = ({ item }) => {
  const [ouverts, setOuverts] = useState(() => new Set(OUVERTS_D_OFFICE));
  const basculer = (cle) => setOuverts((avant) => {
    const apres = new Set(avant);
    if (apres.has(cle)) apres.delete(cle); else apres.add(cle);
    return apres;
  });
  return (
    <div role="group" aria-label={`${nomCourtDeLItem(item)} ${item.libelle}`} className="py-3 space-y-1.5 border-b border-gray-100 last:border-b-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-xs font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md">
          {nomCourtDeLItem(item)}
        </span>
        <span className="flex-1 min-w-[12rem] text-sm text-gray-800">{item.libelle || item.domaine.nom}</span>
        <span className="text-xs text-gray-500">repassée dans {item.nbQcm}{NB}QCM</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {STATUTS_AFFICHES.map((s) => {
          const nb = item.parStatut[s.cle].length;
          if (nb === 0) return <span key={s.cle} className="px-2 py-0.5 rounded-md text-xs font-semibold bg-gray-50 text-gray-300">{s.libelle} 0</span>;
          return (
            <button
              key={s.cle}
              type="button"
              aria-expanded={ouverts.has(s.cle)}
              onClick={() => basculer(s.cle)}
              className={`px-2 py-0.5 rounded-md text-xs font-semibold hover:ring-1 hover:ring-current ${s.style}`}
            >
              {s.libelle} {nb}
            </button>
          );
        })}
      </div>
      {STATUTS_AFFICHES.filter((s) => ouverts.has(s.cle) && item.parStatut[s.cle].length > 0).map((s) => (
        <p key={s.cle} className="text-xs text-gray-600">
          <span className="font-semibold">{s.liste}{NB}:</span> {item.parStatut[s.cle].join(', ')}
        </p>
      ))}
    </div>
  );
};

const CarteRepassees = ({ items }) => {
  const [toutes, setToutes] = useState(false);
  const visibles = toutes ? items : items.slice(0, PREMIERES_QUESTIONS);
  return (
    <Carte icone={Repeat} titre={`Les questions repassées${NB}: qui doit les revoir`} aside={`${items.length} question${items.length > 1 ? 's' : ''}, les plus à reprendre d'abord`}>
      <div className="px-5 py-3">
        <p className="text-xs text-gray-500 pb-2">
          Pour chaque élève, ses essais dans l'ordre, septembre compris. Acquis{NB}: deux réussites de suite à un QCM (une seule
          pour une réponse à taper ou à construire). À confirmer{NB}: une réussite qui peut venir du hasard (une fois sur quatre
          à un QCM). Fragile{NB}: une erreur après une réussite. À revoir{NB}: jamais réussie. Un clic sur un statut
          montre ses élèves.
        </p>
        {visibles.map((item) => <LigneQuestion key={item.cle} item={item} />)}
        {items.length > PREMIERES_QUESTIONS ? (
          <button type="button" onClick={() => setToutes((v) => !v)} className="mt-2 text-sm font-semibold text-blue-700 hover:text-blue-800">
            {toutes ? 'Voir moins de questions' : `Voir les ${items.length} questions`}
          </button>
        ) : null}
      </div>
    </Carte>
  );
};

const Resume = ({ nbQcm, items }) => {
  const aReprendre = new Set(items.flatMap((i) => [...i.parStatut.a_revoir, ...i.parStatut.fragile]));
  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 px-5 py-4 space-y-1">
      <p className="text-[15px] text-gray-800">
        <span className="font-semibold text-blue-700">{nbQcm} QCM de suivi depuis septembre.</span>{' '}
        {items.length > 0
          ? `${items.length} question${items.length > 1 ? 's' : ''} de septembre repassée${items.length > 1 ? 's' : ''}${NB}: ${aReprendre.size} élève${aReprendre.size > 1 ? 's ont' : ' a'} au moins une question à revoir ou fragile.`
          : `Ils ne reprennent encore aucune question de septembre.`}
      </p>
    </div>
  );
};

const DepuisSeptembre = ({ evalNat, bilans }) => {
  const items = useMemo(() => questionsRepassees({ evalNat, bilans }), [evalNat, bilans]);

  if (!bilans || bilans.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 px-5 py-6 text-sm text-gray-600">
        Aucun QCM de suivi n'a encore été passé par cette classe{NB}: la comparaison avec septembre apparaitra après le premier.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Resume nbQcm={bilans.length} items={items} />
      {items.length > 0 ? <CarteRepassees items={items} /> : null}
    </div>
  );
};

export default DepuisSeptembre;
